import { useState, useEffect } from 'react'

function LineChart({ data, labels, color, prefix = '', suffix = '', highlightIdx, label, deltaTxt }: {
  data: number[]
  labels: string[]
  color: string
  prefix?: string
  suffix?: string
  highlightIdx?: number
  label: string
  deltaTxt?: string
}) {
  const max = Math.max(...data) * 1.15
  const w = 720, h = 220, padX = 56, padY = 28
  const innerW = w - padX * 2
  const innerH = h - padY * 2
  const points = data.map((d, i) => {
    const x = padX + (i / (data.length - 1)) * innerW
    const y = padY + innerH - (d / max) * innerH
    return [x, y]
  })
  const areaD = `M ${points[0][0]} ${padY + innerH} ` +
    points.map(p => `L ${p[0]} ${p[1]}`).join(' ') +
    ` L ${points[points.length - 1][0]} ${padY + innerH} Z`
  const lineD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p[0]} ${p[1]}`).join(' ')
  const fmt = (n: number) => prefix + (n >= 1000 ? (n/1000).toFixed(1).replace('.0','') + 'k' : n.toString()) + suffix
  const yTicks = [0, max * 0.5, max].map(v => Math.round(v))

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 8 }}>
        <span className="kpi-label">{label}</span>
        {deltaTxt && <span style={{ fontFamily: 'var(--font-heading)', fontSize: '1.1rem', color }}>{deltaTxt}</span>}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 'auto', display: 'block' }} preserveAspectRatio="xMidYMid meet">
        {yTicks.map((v, i) => {
          const y = padY + innerH - (v / max) * innerH
          return (
            <g key={i}>
              <line x1={padX} y1={y} x2={padX + innerW} y2={y} stroke="var(--border-color)" strokeWidth={1} strokeDasharray={i === 0 ? '' : '2,3'} />
              <text x={padX - 8} y={y + 4} textAnchor="end" fontSize={10} fill="var(--text-muted)" fontFamily="var(--font-mono)">{fmt(v)}</text>
            </g>
          )
        })}
        <path d={areaD} fill={color} opacity={0.12} />
        <path d={lineD} stroke={color} strokeWidth={2.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={p[0]} cy={p[1]} r={i === highlightIdx ? 6 : 3.5} fill={color} stroke="var(--bg-main)" strokeWidth={2} />
            {i === highlightIdx && (
              <text x={p[0]} y={p[1] - 14} textAnchor="middle" fontSize={11} fontWeight={700} fill={color} fontFamily="var(--font-mono)">{fmt(data[i])}</text>
            )}
          </g>
        ))}
        {labels.map((l, i) => {
          const x = padX + (i / (data.length - 1)) * innerW
          return <text key={i} x={x} y={h - 6} textAnchor="middle" fontSize={10} fill="var(--text-muted)" fontFamily="var(--font-mono)">{l}</text>
        })}
      </svg>
    </div>
  )
}

function StackedBar({ items, label, total }: {
  items: { label: string; value: number; color: string }[]
  label: string
  total?: number
}) {
  const sum = items.reduce((a, b) => a + b.value, 0)
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
        <span className="kpi-label">{label}</span>
        {total !== undefined && <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>n = {total}</span>}
      </div>
      <div style={{ display: 'flex', height: 32, borderRadius: 2, overflow: 'hidden', border: '1px solid var(--border-color)' }}>
        {items.map((it, i) => (
          <div key={i} style={{
            width: `${(it.value / sum) * 100}%`,
            background: it.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            fontWeight: 700,
          }}>
            {((it.value / sum) * 100) >= 8 && `${((it.value / sum) * 100).toFixed(1)}%`}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap' }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
            <span style={{ width: 10, height: 10, background: it.color, display: 'inline-block', borderRadius: 1 }} />
            <span>{it.label}: <strong style={{ color: 'var(--text-main)' }}>{it.value}</strong></span>
          </div>
        ))}
      </div>
    </div>
  )
}

const FUNIL_MONTHS = ['Set/25', 'Out/25', 'Nov/25', 'Dez/25', 'Jan/26', 'Fev/26', 'Mar/26', 'Abr/26']
const CUSTO_CQL = [84, 80, 66, 77, 66, 70, 67, 55]
const CQL_META = [7852, 7661, 8372, 6239, 7327, 6370, 9441, 11530]
const CRM_MONTHS = ['Set/25', 'Out/25', 'Nov/25', 'Dez/25', 'Jan/26', 'Fev/26', 'Mar/26', 'Abr/26', 'Mai/26*']
const CQL_CRM = [871, 738, 1129, 899, 970, 1010, 2296, 3448, 2821]

const SECTIONS = [
  { id: 'sec-01', label: 'TL;DR' },
  { id: 'sec-02', label: 'FUNIL META' },
  { id: 'sec-03', label: 'CRM' },
  { id: 'sec-04', label: 'FORMS WP' },
  { id: 'sec-05', label: 'FALLBACK' },
  { id: 'sec-06', label: 'CWV' },
  { id: 'sec-07', label: 'CYBER' },
  { id: 'sec-08', label: 'SEO' },
  { id: 'sec-09', label: 'UPSIDE' },
  { id: 'sec-10', label: 'ROADMAP' },
]

const STYLES = `
:root {
  --bg-main: #050508;
  --bg-panel: #0C0E14;
  --border-color: #1A1D2E;
  --primary: #00FF88;
  --secondary: #3B82F6;
  --alert: #F59E0B;
  --danger: #EF4444;
  --text-main: #E2E8F0;
  --text-muted: #94A3B8;
  --grid-color: rgba(30, 41, 59, 0.3);
  --font-heading: 'JetBrains Mono', monospace;
  --font-body: 'IBM Plex Sans', sans-serif;
  --font-mono: 'JetBrains Mono', monospace;
  --font-label: 'IBM Plex Mono', monospace;
}
.bg-mc { background-color: var(--bg-main); color: var(--text-main); font-family: var(--font-body); line-height: 1.6; min-height: 100vh; cursor: crosshair; }
.bg-mc::before {
  content: ""; position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background-image: linear-gradient(to right, var(--grid-color) 1px, transparent 1px), linear-gradient(to bottom, var(--grid-color) 1px, transparent 1px);
  background-size: 40px 40px; z-index: 0; pointer-events: none;
}
.scanlines {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: repeating-linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.02) 0.5%, transparent 1%);
  z-index: 9999; pointer-events: none; animation: scanlineScroll 20s linear infinite;
}
@keyframes scanlineScroll { 0% { background-position: 0 0; } 100% { background-position: 0 100vh; } }

.mc-h1, .mc-h2, .mc-h3, .mc-h4, .font-heading { font-family: var(--font-heading); font-weight: 700; }
.font-mono { font-family: var(--font-mono); }
.font-label { font-family: var(--font-label); text-transform: uppercase; letter-spacing: 3px; font-size: 0.75rem; }
.text-primary { color: var(--primary); }
.text-secondary { color: var(--secondary); }
.text-alert { color: var(--alert); }
.text-danger { color: var(--danger); }
.text-muted { color: var(--text-muted); }

.glitch-hover { transition: all 0.2s ease; display: inline-block; }
.glitch-hover:hover { text-shadow: 2px 0 var(--danger), -2px 0 var(--secondary); transform: skewX(-2deg); }

.app-container { display: flex; min-height: 100vh; padding-bottom: 40px; position: relative; z-index: 1; }

.sidebar {
  width: 60px; background-color: var(--bg-panel); border-right: 1px solid var(--border-color);
  position: fixed; top: 0; left: 0; height: 100vh; display: flex; flex-direction: column;
  align-items: center; padding: 20px 0; z-index: 100;
}
.nav-item {
  width: 40px; height: 40px; margin-bottom: 15px; display: flex; justify-content: center; align-items: center;
  color: var(--text-muted); background: transparent; border: 1px solid transparent; cursor: pointer;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700; transition: all 0.3s ease; position: relative;
}
.nav-item:hover, .nav-item.active { color: var(--primary); border-color: var(--primary); background: rgba(0,255,136,0.1); }
.nav-item .tooltip {
  position: absolute; left: 50px; background: var(--bg-panel); border: 1px solid var(--primary);
  color: var(--primary); padding: 4px 8px; font-family: var(--font-label); font-size: 10px;
  white-space: nowrap; opacity: 0; pointer-events: none; transform: translateX(-10px); transition: all 0.2s ease;
}
.nav-item:hover .tooltip { opacity: 1; transform: translateX(0); }

.progress-rail { position: fixed; top: 0; left: 0; right: 0; height: 2px; background: rgba(0,0,0,0); z-index: 1000; }
.progress-fill-bar { height: 100%; background: var(--primary); width: 0%; transition: width 0.1s linear; }

.main-content { flex: 1; margin-left: 60px; padding: 40px 60px; max-width: 1280px; }

section.mc { margin-bottom: 80px; }
.section-header { margin-bottom: 30px; border-bottom: 1px solid var(--border-color); padding-bottom: 10px; }
.section-tag { font-family: var(--font-mono); color: var(--text-muted); font-size: 0.85rem; margin-bottom: 10px; display: block; }
.section-title { font-size: 2.3rem; color: var(--text-main); margin-bottom: 5px; font-family: var(--font-heading); }
.section-subtitle { font-family: var(--font-mono); color: var(--text-muted); font-size: 0.95rem; }

.panel { background-color: var(--bg-panel); border: 1px solid var(--border-color); padding: 24px; position: relative; transition: border-color 0.3s ease, box-shadow 0.3s ease; }
.panel::before, .panel::after { content: ''; position: absolute; width: 10px; height: 10px; border: 1px solid var(--text-muted); transition: border-color 0.3s; }
.panel::before { top: -1px; left: -1px; border-right: none; border-bottom: none; }
.panel::after { bottom: -1px; right: -1px; border-left: none; border-top: none; }
.panel:hover::before, .panel:hover::after { border-color: var(--primary); }

.grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
.grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }

.status-tag { display: inline-flex; align-items: center; gap: 8px; padding: 4px 8px; font-family: var(--font-label); border: 1px solid transparent; background: rgba(255,255,255,0.05); }
.status-tag.green { color: var(--primary); border-color: rgba(0,255,136,0.3); background: rgba(0,255,136,0.05); }
.status-tag.amber { color: var(--alert); border-color: rgba(245,158,11,0.3); background: rgba(245,158,11,0.05); }
.status-tag.red { color: var(--danger); border-color: rgba(239,68,68,0.3); background: rgba(239,68,68,0.05); }
.status-tag.blue { color: var(--secondary); border-color: rgba(59,130,246,0.3); background: rgba(59,130,246,0.05); }

.dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
.dot.green { background: var(--primary); box-shadow: 0 0 8px var(--primary); animation: pulseDot 2s infinite; }
.dot.amber { background: var(--alert); box-shadow: 0 0 8px var(--alert); animation: pulseDot 2s infinite; }
.dot.red { background: var(--danger); box-shadow: 0 0 8px var(--danger); animation: pulseDot 2s infinite; }
@keyframes pulseDot { 0% { transform: scale(0.95); opacity: 0.8; } 50% { transform: scale(1.2); opacity: 1; } 100% { transform: scale(0.95); opacity: 0.8; } }

table.mc { width: 100%; border-collapse: collapse; margin-top: 15px; font-family: var(--font-body); font-size: 0.9rem; }
table.mc th, table.mc td { padding: 10px 12px; text-align: left; border-bottom: 1px solid var(--border-color); }
table.mc th { font-family: var(--font-label); color: var(--text-muted); font-size: 0.7rem; text-transform: uppercase; }
table.mc tr:hover td { background: rgba(255,255,255,0.02); }
table.mc td.num, table.mc th.num { text-align: right; font-family: var(--font-mono); }
table.mc tr.highlight td { background: rgba(0,255,136,0.05); }

.callout { padding: 18px 20px; margin: 18px 0; background: var(--bg-panel); border-left: 4px solid; font-family: var(--font-mono); font-size: 0.92rem; line-height: 1.7; }
.callout.amber { border-color: var(--alert); }
.callout.blue { border-color: var(--secondary); background: rgba(59,130,246,0.05); }
.callout.green { border-color: var(--primary); }
.callout.red { border-color: var(--danger); background: rgba(239,68,68,0.08); color: #fff; }

.terminal-block { background: #000; border: 1px solid var(--border-color); padding: 16px; font-family: var(--font-mono); font-size: 0.82rem; color: var(--text-muted); white-space: pre; overflow-x: auto; line-height: 1.8; }

.big-num { font-family: var(--font-heading); font-size: 3rem; line-height: 1; letter-spacing: -2px; }
.big-num.primary { color: var(--primary); }
.big-num.alert { color: var(--alert); }
.big-num.secondary { color: var(--secondary); }
.big-num.danger { color: var(--danger); }
.kpi-label { font-family: var(--font-mono); font-size: 0.7rem; color: var(--text-muted); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 1px; }
.kpi-sub { font-size: 0.75rem; color: var(--text-muted); margin-top: 8px; font-family: var(--font-mono); }

.status-bar { position: fixed; bottom: 0; left: 0; width: 100%; height: 40px; background: var(--bg-panel); border-top: 1px solid var(--border-color); display: flex; justify-content: space-between; align-items: center; padding: 0 20px; font-family: var(--font-mono); font-size: 0.78rem; z-index: 100; }

@media (max-width: 900px) {
  .sidebar { width: 100%; height: 60px; flex-direction: row; justify-content: space-around; top: auto; bottom: 40px; border-right: none; border-top: 1px solid var(--border-color); overflow-x: auto; }
  .nav-item { margin-bottom: 0; flex-shrink: 0; }
  .nav-item .tooltip { display: none; }
  .main-content { margin-left: 0; padding: 20px; padding-bottom: 120px; }
  .grid-3, .grid-2, .grid-4 { grid-template-columns: 1fr; }
  .section-title { font-size: 1.7rem; }
  .big-num { font-size: 2.2rem; }
}
`

export default function BlipsGrowth() {
  const [activeSection, setActiveSection] = useState('sec-01')
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    document.title = 'Blips Growth Engineering — Mission Control'
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600&family=IBM+Plex+Sans:ital,wght@0,400;0,600;1,400&family=JetBrains+Mono:wght@400;700&display=swap'
    document.head.appendChild(link)

    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex, nofollow'
    document.head.appendChild(meta)

    return () => {
      document.head.removeChild(link)
      document.head.removeChild(meta)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      let current = ''
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id)
        if (el) {
          const rect = el.getBoundingClientRect()
          if (rect.top <= 250) current = s.id
        }
      }
      if (current && current !== activeSection) setActiveSection(current)
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      setProgress(docHeight > 0 ? (window.scrollY / docHeight) * 100 : 0)
    }
    const handleKey = (e: KeyboardEvent) => {
      const idx = SECTIONS.findIndex(s => s.id === activeSection)
      if ((e.key === 'ArrowRight' || e.key === 'ArrowDown') && idx < SECTIONS.length - 1) {
        e.preventDefault(); scrollTo(SECTIONS[idx + 1].id)
      }
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && idx > 0) {
        e.preventDefault(); scrollTo(SECTIONS[idx - 1].id)
      }
    }
    window.addEventListener('scroll', handleScroll)
    window.addEventListener('keydown', handleKey)
    handleScroll()
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('keydown', handleKey)
    }
  }, [activeSection])

  const scrollTo = (id: string) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <div className="bg-mc">
      <style>{STYLES}</style>
      <div className="scanlines" />
      <div className="progress-rail"><div className="progress-fill-bar" style={{ width: `${progress}%` }} /></div>

      <div className="app-container">
        <nav className="sidebar">
          {SECTIONS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => scrollTo(s.id)}
              className={`nav-item ${activeSection === s.id ? 'active' : ''}`}
              aria-label={s.label}
            >
              {String(i + 1).padStart(2, '0')}
              <span className="tooltip">{s.label}</span>
            </button>
          ))}
        </nav>

        <main className="main-content">

          {/* SEC-01 — TL;DR */}
          <section id="sec-01" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-01] // CAPITAL PRESERVADO + CAPITAL DESTRAVÁVEL</span>
              <h2 className="section-title glitch-hover">Blips Growth Engineering</h2>
              <p className="section-subtitle">Set/2025 → Jun/2026 (em curso) · 9 meses · verba estável · mesma equipe técnica</p>
            </div>

            <div className="panel" style={{ marginBottom: 24, padding: '36px 28px' }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '1.45rem', lineHeight: 1.55, color: 'var(--text-main)', margin: 0 }}>
                Em 8 meses, a mesma verba comprou <span className="text-primary">+45,9% mais leads qualificados</span><br/>
                — e a infraestrutura ativa segura <span className="text-primary">R$ 30-50k/dia</span><br/>
                de fluxo de caixa que não parou de entrar.
              </p>
              <p className="text-muted" style={{ marginTop: 18, fontFamily: 'var(--font-mono)', fontSize: '0.88rem' }}>
                A pergunta não é o que mudou. É <span className="text-primary">o que ainda pode mudar</span>.
              </p>
            </div>

            <div className="panel" style={{ padding: '28px 28px 24px' }}>
              <LineChart
                data={CUSTO_CQL}
                labels={FUNIL_MONTHS}
                color="#00FF88"
                prefix="R$"
                label="CUSTO POR CQL — META + GOOGLE (R$)"
                highlightIdx={7}
                deltaTxt="−34,5% em 8 meses"
              />
              <p className="text-muted" style={{ marginTop: 8, fontSize: '0.75rem', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                Pico Set/25: R$84 · Mínimo Abr/26: R$55 · Mediana 8m: R$70 · eixo Y começa em zero
              </p>
            </div>

            <div className="grid-3" style={{ marginTop: 24 }}>
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">CQL / Mês</div>
                <div className="big-num primary">+45,9%</div>
                <div className="kpi-sub">7.852 → 11.530 · mesma verba</div>
              </div>
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">Custo por CQL</div>
                <div className="big-num primary">−34,5%</div>
                <div className="kpi-sub">R$84 → R$55 · eficiência técnica</div>
              </div>
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">N2 em Pipeline</div>
                <div className="big-num secondary">+561%</div>
                <div className="kpi-sub">36 → 238 deals · pipeline cheio</div>
              </div>
            </div>

            <div className="callout green" style={{ marginTop: 24 }}>
              <strong className="text-primary">Capital protegido — continuamente:</strong> mesma verba (R$657k → R$628k/mês), mesma equipe técnica. O ganho vem da <strong className="text-primary">manutenção ativa</strong> de performance, forms, fallback e cyber — frentes que continuam rodando 24/7.
            </div>

            <div className="callout blue">
              <strong className="text-secondary">Gancho de negócio:</strong> LCP saiu de 10s → 4s nas LPs principais. Google: LCP &lt;2,5s melhora Quality Score → reduz CPC. Cada <strong>1pp adicional</strong> de Quality Score = <strong className="text-primary">R$ 6,3k/mês</strong> economizados na carteira atual.
            </div>

            <div className="panel" style={{ marginTop: 24, borderColor: 'var(--primary)', borderWidth: '2px' }}>
              <div className="kpi-label" style={{ color: 'var(--primary)' }}>★ Sangramento diário evitado pela infraestrutura atual</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 16 }}>
                <div>
                  <div className="big-num primary" style={{ fontSize: '3.5rem' }}>R$ 30-50k</div>
                  <div className="kpi-sub" style={{ marginTop: 8 }}>por dia · valor preservado pelo conjunto técnico ativo</div>
                </div>
                <table className="mc" style={{ flex: '1 1 480px', minWidth: 380, margin: 0 }}>
                  <thead>
                    <tr><th>Frente</th><th className="num">R$ / dia preservado</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Fallback recorrente (600 CNPJ/mês)</td><td className="num text-primary"><strong>R$ 14.000</strong></td></tr>
                    <tr><td>Core Web Vitals + Quality Score</td><td className="num text-primary"><strong>R$ 10-17k</strong></td></tr>
                    <tr><td>SEO orgânico (recovery em curso)</td><td className="num text-primary"><strong>R$ 7.700</strong></td></tr>
                    <tr><td>Cyber sustentado (WAF + SCAN-BLIPS)</td><td className="num text-primary"><strong>R$ 6.700</strong></td></tr>
                    <tr><td>Forms WP otimizados (Ideal escalado)</td><td className="num text-primary"><strong>R$ 833</strong></td></tr>
                  </tbody>
                </table>
              </div>
              <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 16, lineHeight: 1.7, fontFamily: 'var(--font-mono)' }}>
                Cálculo: cada frente convertida em CQL → Won → LTV (premissas Sec-05). Range absorve overlap entre métricas.
                Sangramento = valor que seria perdido por dia se a infraestrutura atual fosse desativada/voltasse ao estado pré-otimização.
              </p>
            </div>

            <div className="grid-4" style={{ marginTop: 24 }}>
              <div className="panel" style={{ padding: 16 }}>
                <div className="kpi-label">Verba mensal</div>
                <div className="font-heading" style={{ fontSize: '1.4rem' }}>R$ 628k</div>
                <div className="kpi-sub">Meta + Google · estável</div>
              </div>
              <div className="panel" style={{ padding: 16 }}>
                <div className="kpi-label">LPs Otimizadas</div>
                <div className="font-heading" style={{ fontSize: '1.4rem' }}>251</div>
                <div className="kpi-sub">9 domínios WordPress</div>
              </div>
              <div className="panel" style={{ padding: 16 }}>
                <div className="kpi-label">Cache Cloudflare</div>
                <div className="font-heading" style={{ fontSize: '1.4rem' }}>25,9M</div>
                <div className="kpi-sub">requests/mês · 77-90% hit</div>
              </div>
              <div className="panel" style={{ padding: 16 }}>
                <div className="kpi-label">Risco SEO Evitado</div>
                <div className="font-heading text-alert" style={{ fontSize: '1.4rem' }}>R$ 3,5M</div>
                <div className="kpi-sub">projeção 3 anos · 331 URLs spam</div>
              </div>
            </div>
          </section>

          {/* SEC-02 — FUNIL META+GOOGLE */}
          <section id="sec-02" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-02] // FUNIL META + GOOGLE</span>
              <h2 className="section-title glitch-hover">Eficiência por CQL</h2>
              <p className="section-subtitle">Custo/CQL: R$84 → R$55 (−35%) em 8 meses · verba estável</p>
            </div>

            <div className="panel">
              <table className="mc">
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th className="num">Verba</th>
                    <th className="num">Impressões</th>
                    <th className="num">Cliques</th>
                    <th className="num">Leads</th>
                    <th className="num">CPL</th>
                    <th className="num">CQL</th>
                    <th className="num">Custo/CQL</th>
                    <th className="num">Ganho</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Set 2025</td><td className="num">R$657k</td><td className="num">21,5M</td><td className="num">245k</td><td className="num">14,0k</td><td className="num">R$47</td><td className="num">7,9k</td><td className="num text-danger">R$84</td><td className="num">623</td></tr>
                  <tr><td>Out 2025</td><td className="num">R$612k</td><td className="num">18,8M</td><td className="num">221k</td><td className="num">12,6k</td><td className="num">R$49</td><td className="num">7,7k</td><td className="num">R$80</td><td className="num">618</td></tr>
                  <tr><td>Nov 2025</td><td className="num">R$550k</td><td className="num">16,9M</td><td className="num">208k</td><td className="num">12,7k</td><td className="num">R$43</td><td className="num">8,4k</td><td className="num">R$66</td><td className="num">641</td></tr>
                  <tr><td>Dez 2025</td><td className="num">R$479k</td><td className="num">17,0M</td><td className="num">186k</td><td className="num">11,3k</td><td className="num">R$42</td><td className="num">6,2k</td><td className="num">R$77</td><td className="num">501</td></tr>
                  <tr><td>Jan 2026</td><td className="num">R$483k</td><td className="num">20,5M</td><td className="num">226k</td><td className="num">13,6k</td><td className="num">R$36</td><td className="num">7,3k</td><td className="num">R$66</td><td className="num">510</td></tr>
                  <tr><td>Fev 2026</td><td className="num">R$446k</td><td className="num">19,6M</td><td className="num">255k</td><td className="num">11,6k</td><td className="num">R$38</td><td className="num">6,4k</td><td className="num">R$70</td><td className="num">390</td></tr>
                  <tr><td>Mar 2026</td><td className="num">R$631k</td><td className="num">26,9M</td><td className="num">320k</td><td className="num">16,1k</td><td className="num">R$39</td><td className="num">9,4k</td><td className="num">R$67</td><td className="num text-primary"><strong>756</strong></td></tr>
                  <tr className="highlight"><td><strong>Abr 2026 ★</strong></td><td className="num">R$628k</td><td className="num">27,9M</td><td className="num">377k</td><td className="num">13,4k</td><td className="num">R$47</td><td className="num"><strong className="text-primary">11,5k</strong></td><td className="num"><strong className="text-primary">R$55</strong></td><td className="num">574</td></tr>
                </tbody>
              </table>
            </div>

            <div className="grid-2" style={{ marginTop: 24 }}>
              <div className="panel">
                <div className="kpi-label">Leituras-chave</div>
                <div className="terminal-block" style={{ marginTop: 12 }}>
{`VERBA          R$657k → R$628k    estável (−4%)
IMPRESSÕES     21,5M → 27,9M      +30%  (mesma verba)
CLIQUES        245k → 377k        +54%  (CTR domain-wide)
CQL            7,9k → 11,5k       +46%  (qualidade CRM)
CUSTO/CQL      R$84 → R$55        −35%  (eficiência)`}
                </div>
              </div>
              <div className="panel">
                <div className="kpi-label">Premissa do ganho</div>
                <p className="text-muted" style={{ marginTop: 10, fontSize: '0.92rem' }}>
                  Mesma verba, mesma equipe. Ganho vem de 3 frentes:
                </p>
                <ul style={{ marginTop: 12, paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 2 }}>
                  <li>Performance LPs (+5,2 pts PageSpeed → +CTR → +Quality Score)</li>
                  <li>Forms otimizados (Ideal Distribuidora +55,9%, escalando)</li>
                  <li>Fallback recuperando 600 CNPJ/mês adicionais</li>
                </ul>
                <div className="callout green" style={{ marginTop: 16 }}>
                  <strong>Upside:</strong> Custo/CQL R$40-45 com reescrita das 7 LPs B2B fora do top 10 = <strong className="text-primary">+R$80-110k/mês</strong> em valor de mídia equivalente.
                </div>
              </div>
            </div>
          </section>

          {/* SEC-03 — CRM PIPEDRIVE */}
          <section id="sec-03" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-03] // FUNIL CRM PIPEDRIVE</span>
              <h2 className="section-title glitch-hover">Volume + Qualidade no CRM</h2>
              <p className="section-subtitle">Set/25 → Mai/26 · +386,5% Leads CRM (1.022 → 4.972) · +561% N2 ativos (36 → 238)</p>
            </div>

            <div className="panel" style={{ padding: '28px 28px 24px', marginBottom: 24 }}>
              <LineChart
                data={CQL_CRM}
                labels={CRM_MONTHS}
                color="#3B82F6"
                label="CQL JP NO PIPEDRIVE — VOLUME ABSOLUTO"
                highlightIdx={7}
                deltaTxt="+295,9% em 9 meses"
              />
              <p className="text-muted" style={{ marginTop: 8, fontSize: '0.75rem', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                Set/25: 871 CQLs · Abr/26: 3.448 CQLs (pico) · Mai/26*: 2.821 (parcial) · curva exponencial pós-Fev/26
              </p>
            </div>

            <div className="panel">
              <table className="mc">
                <thead>
                  <tr>
                    <th>Mês</th>
                    <th className="num">Leads CRM</th>
                    <th className="num">CQL JP</th>
                    <th className="num">N1</th>
                    <th className="num">N2</th>
                    <th className="num">Ganho</th>
                    <th className="num">CQL%</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Set/25</td><td className="num">1.022</td><td className="num">871</td><td className="num">365</td><td className="num">36</td><td className="num">476</td><td className="num text-primary">85,2%</td></tr>
                  <tr><td>Out/25</td><td className="num">828</td><td className="num">738</td><td className="num">268</td><td className="num">13</td><td className="num">510</td><td className="num text-primary">89,1%</td></tr>
                  <tr><td>Nov/25</td><td className="num">1.238</td><td className="num">1.129</td><td className="num">527</td><td className="num">19</td><td className="num">497</td><td className="num text-primary"><strong>91,2%</strong></td></tr>
                  <tr><td>Dez/25</td><td className="num">997</td><td className="num">899</td><td className="num">537</td><td className="num">42</td><td className="num">383</td><td className="num text-primary">90,2%</td></tr>
                  <tr><td>Jan/26</td><td className="num">1.194</td><td className="num">970</td><td className="num">840</td><td className="num">107</td><td className="num">383</td><td className="num text-alert">81,2%</td></tr>
                  <tr><td>Fev/26</td><td className="num">1.309</td><td className="num">1.010</td><td className="num">672</td><td className="num">43</td><td className="num">295</td><td className="num text-alert">77,2%</td></tr>
                  <tr><td>Mar/26</td><td className="num"><strong>3.517</strong></td><td className="num">2.296</td><td className="num">2.548</td><td className="num">82</td><td className="num">550</td><td className="num text-danger">65,3%</td></tr>
                  <tr className="highlight"><td><strong>Abr/26 ★</strong></td><td className="num"><strong className="text-primary">4.972</strong></td><td className="num"><strong className="text-primary">3.448</strong></td><td className="num">2.614</td><td className="num"><strong className="text-primary">238</strong></td><td className="num">443</td><td className="num text-alert">69,3%</td></tr>
                  <tr style={{ opacity: 0.6 }}><td>Mai/26*</td><td className="num">4.175</td><td className="num">2.821</td><td className="num">2.290</td><td className="num">438</td><td className="num">155</td><td className="num text-alert">67,6%</td></tr>
                  <tr style={{ opacity: 0.5 }}><td>Jun/26*</td><td className="num">—</td><td className="num">—</td><td className="num">—</td><td className="num">—</td><td className="num">—</td><td className="num text-muted">em curso</td></tr>
                </tbody>
              </table>
              <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 12, fontFamily: 'var(--font-mono)' }}>* Mai/26 parcial — ciclo de vendas em aberto</p>
            </div>

            <div className="grid-2" style={{ marginTop: 24 }}>
              <div className="panel">
                <div className="kpi-label">Crescimento mensal</div>
                <div className="terminal-block" style={{ marginTop: 12 }}>
{`LEADS CRM      1.022 → 4.972    +387%  (5x — funil técnico)
CQL ABSOLUTO   871   → 3.448    +296%  (4x qualificados)
N2 ATIVOS      36    → 238      +561%  (pipeline cheio)
GANHO          476   → 443      estável (ciclo em aberto)`}
                </div>
              </div>
              <div className="panel">
                <div className="kpi-label">Sobre a queda CQL% (91,2 → 67,6)</div>
                <p className="text-muted" style={{ marginTop: 10, fontSize: '0.9rem', lineHeight: 1.8 }}>
                  Volume cresceu <strong>4-5×</strong>. Lei dos grandes números esperada. CQL absoluto SUBIU 4× → mais qualificados em valor absoluto. A queda % é <strong className="text-primary">endereçável</strong>.
                </p>
                <div className="callout green" style={{ marginTop: 12 }}>
                  <strong>Upside:</strong> Recuperar CQL% pra 80%+ com volume atual = <strong className="text-primary">+1.000 CQLs/mês</strong> (~R$55k/mês em valor de mídia).
                </div>
              </div>
            </div>
          </section>

          {/* SEC-04 — FORMS WORDPRESS */}
          <section id="sec-04" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-04] // FORMS WORDPRESS</span>
              <h2 className="section-title glitch-hover">Caso Ideal Distribuidora</h2>
              <p className="section-subtitle">+55,9% submissões em 7 meses · sem aumento de tráfego pago</p>
            </div>

            <div className="grid-3">
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">Ecosolvente</div>
                <div className="big-num primary">+271%</div>
                <div className="kpi-sub">submissões / mês</div>
              </div>
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">FlatBed UV</div>
                <div className="big-num primary">+186%</div>
                <div className="kpi-sub">submissões / mês</div>
              </div>
              <div className="panel" style={{ textAlign: 'center' }}>
                <div className="kpi-label">CNC 6040</div>
                <div className="big-num primary">+144%</div>
                <div className="kpi-sub">submissões / mês</div>
              </div>
            </div>

            <div className="panel" style={{ marginTop: 24 }}>
              <table className="mc">
                <thead>
                  <tr>
                    <th>Métrica</th>
                    <th className="num">Antes</th>
                    <th className="num">Depois</th>
                    <th className="num">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>Submissões totais (domain)</td><td className="num">1.162</td><td className="num"><strong>1.811</strong></td><td className="num text-primary"><strong>+55,9%</strong></td></tr>
                  <tr><td>Leads adicionais/mês</td><td className="num">—</td><td className="num">+649</td><td className="num text-primary">+R$25,3k em mídia</td></tr>
                  <tr><td>Acumulado 7 meses (CPL R$39,84)</td><td className="num">—</td><td className="num">—</td><td className="num text-primary"><strong>R$177k</strong></td></tr>
                </tbody>
              </table>
            </div>

            <div className="callout blue">
              <strong>Sem aumento de tráfego pago.</strong> Puramente otimização de LP, validação de campos e arquitetura de formulário. Mesma verba Meta, +649 leads/mês.
            </div>

            <div className="callout green">
              <strong>Upside:</strong> mesma arquitetura aplicada em 8 LPs restantes dos outros domínios = projeção <strong className="text-primary">+1.500-2.000 envios/mês adicionais</strong>.
            </div>
          </section>

          {/* SEC-05 — FALLBACK */}
          <section id="sec-05" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-05] // SISTEMA DE FALLBACK</span>
              <h2 className="section-title glitch-hover">Recuperação de Leads · Resultado Final</h2>
              <p className="section-subtitle">7.600 leads salvos → ~5.700 CQLs → ~400 deals esperados · R$ 4M em receita potencial</p>
            </div>

            <div className="panel">
              <div className="kpi-label">Tradução em resultado final (não só mídia)</div>
              <table className="mc">
                <thead>
                  <tr>
                    <th>Evento</th>
                    <th className="num">Leads salvos</th>
                    <th className="num">CQLs esperados<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(× 76,4%)</span></th>
                    <th className="num">Deals esperados<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(× 7,1%)</span></th>
                    <th className="num">Receita potencial<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(LTV R$10k)</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Nov-Dez 2025</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>falha de integração</span></td>
                    <td className="num">5.000</td>
                    <td className="num text-secondary">~3.820</td>
                    <td className="num text-primary"><strong>~271</strong></td>
                    <td className="num text-primary"><strong>R$ 2,71M</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Abr 2026</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>falha webhook · 6 dias</span></td>
                    <td className="num">2.000</td>
                    <td className="num text-secondary">~1.528</td>
                    <td className="num text-primary"><strong>~108</strong></td>
                    <td className="num text-primary"><strong>R$ 1,08M</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Recorrente / mês</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>CNPJ válido automático</span></td>
                    <td className="num">600</td>
                    <td className="num text-secondary">~600 <span className="text-muted" style={{ fontSize: '0.7rem' }}>(já CQL)</span></td>
                    <td className="num text-primary"><strong>~43 / mês</strong></td>
                    <td className="num text-primary"><strong>R$ 430k / mês</strong></td>
                  </tr>
                  <tr className="highlight">
                    <td><strong>TOTAL ACUMULADO + ANUALIZADO</strong></td>
                    <td className="num"><strong>7.600 + 7.200/ano</strong></td>
                    <td className="num"><strong>~5.700 + 7.200/ano</strong></td>
                    <td className="num text-primary"><strong>~400 + 510/ano</strong></td>
                    <td className="num text-primary"><strong>~R$ 4M + R$ 5,1M/ano</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="callout blue">
              <strong>Premissas conservadoras (validar com Pipedrive histórico):</strong><br/>
              CQL% médio do funil Set/25→Mai/26 = <strong>76,4%</strong> · CQL→Won = <strong>7,1%</strong> (4.613 ganhos / 64.800 CQLs) · LTV B2B = <strong>R$10k</strong> (placeholder — alinhar com CFO).<br/>
              Connect rate + conversão por form a serem confirmados com base histórica.
            </div>

            <div className="panel" style={{ marginTop: 24 }}>
              <div className="kpi-label">Arquitetura</div>
              <p className="text-muted" style={{ marginTop: 12, fontSize: '0.95rem', lineHeight: 1.9 }}>
                Lock pattern + retry queue + half-submission rescue. Leads antes perdidos por falha de integração são capturados assincronamente. Base histórica desde 2021 estruturada gera mensalmente lista para enriquecimento via big data.
              </p>
            </div>

            <div className="callout green">
              <strong>Upside:</strong> 1.200 leads/mês recuperados com fallback cross-domain (atual: domain-isolated) + esteira de half-submissions em 100% dos forms (atual: 60%) = <strong>+86 deals/mês potencial</strong> = ~R$ 860k/mês.
            </div>
          </section>

          {/* SEC-06 — CORE WEB VITALS */}
          <section id="sec-06" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-06] // CORE WEB VITALS</span>
              <h2 className="section-title glitch-hover">Performance · 75% das LPs melhoraram</h2>
              <p className="section-subtitle">191 de 254 LPs subiram score · LCP caiu pela metade nas top conversão</p>
            </div>

            <div className="panel" style={{ padding: '28px 28px 20px', marginBottom: 24 }}>
              <StackedBar
                items={[
                  { label: 'Melhoraram', value: 191, color: '#00FF88' },
                  { label: 'Neutras (±5 pts)', value: 54, color: '#94A3B8' },
                  { label: 'Caíram', value: 9, color: '#F59E0B' },
                ]}
                total={254}
                label="DISTRIBUIÇÃO DAS 254 LPs PÓS-OTIMIZAÇÃO"
              />
              <p className="text-muted" style={{ marginTop: 14, fontSize: '0.75rem', fontFamily: 'var(--font-mono)', textAlign: 'center' }}>
                75,2% melhoraram · 21,3% dentro da margem natural do Lighthouse · 3,5% em investigação
              </p>
            </div>

            <div className="panel">
              <table className="mc">
                <thead>
                  <tr>
                    <th>Métrica</th>
                    <th className="num">Antes → Agora</th>
                    <th className="num">Leads extra/mês</th>
                    <th className="num">CQLs<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(×76,4%)</span></th>
                    <th className="num">Deals<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(×7,1%)</span></th>
                    <th className="num">Receita potencial<br/><span style={{ fontWeight: 400, fontSize: '0.65rem', textTransform: 'none' }}>(LTV R$10k)</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>TTFB médio</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>−5-10% bounce mobile</span></td>
                    <td className="num">3.300ms → <span className="text-primary"><strong>50ms</strong></span></td>
                    <td className="num">+400-800</td>
                    <td className="num text-secondary">~305-610</td>
                    <td className="num text-primary"><strong>~22-43</strong></td>
                    <td className="num text-primary"><strong>R$ 220-430k/mês</strong></td>
                  </tr>
                  <tr>
                    <td><strong>LCP (top páginas)</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>+10-15% form starts</span></td>
                    <td className="num">10-11s → <span className="text-primary"><strong>3-5s</strong></span></td>
                    <td className="num">+1.000-1.500</td>
                    <td className="num text-secondary">~760-1.150</td>
                    <td className="num text-primary"><strong>~54-82</strong></td>
                    <td className="num text-primary"><strong>R$ 540-820k/mês</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Score mobile (top 30)</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>Quality Score +5-10pp · CPC −5%</span></td>
                    <td className="num">48 → <span className="text-primary"><strong>72</strong></span></td>
                    <td className="num">+500-1.000</td>
                    <td className="num text-secondary">~380-760</td>
                    <td className="num text-primary"><strong>~27-54</strong></td>
                    <td className="num text-primary"><strong>R$ 270-540k/mês</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Elementor ⇄ CF sync</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>auto-purge sem stale CSS</span></td>
                    <td className="num text-primary"><strong>0 incidentes visuais</strong></td>
                    <td className="num">manutenção</td>
                    <td className="num text-muted">—</td>
                    <td className="num text-muted">—</td>
                    <td className="num text-muted">tempo dev economizado</td>
                  </tr>
                  <tr>
                    <td><strong>Sorvete (case piloto)</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>CPL R$24 vs média R$47</span></td>
                    <td className="num">61 → <span className="text-primary"><strong>79</strong></span></td>
                    <td className="num">replicável</td>
                    <td className="num text-secondary">×7 segmentos</td>
                    <td className="num text-muted">→ ver Sec-09</td>
                    <td className="num text-primary"><strong>case escalável</strong></td>
                  </tr>
                </tbody>
              </table>
              <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 12, fontFamily: 'var(--font-mono)', fontStyle: 'italic' }}>
                Estimativas com overlap entre métricas (TTFB+LCP+QS afetam o mesmo lead). Valor consolidado considerando dedup: <strong className="text-primary">R$ 300-500k/mês potencial bruto</strong>. Validar com A/B Pipedrive + Looker antes/depois.
              </p>
            </div>

            <div className="callout blue">
              <strong className="text-secondary">Frame de negócio:</strong><br/>
              LCP saiu de 10s → 4s nas LPs de fitness e food. Google: LCP &lt;2,5s melhora Quality Score → reduz CPC.<br/>
              Fitness opera com <strong>R$107k/mês</strong> de verba. Cada 1% de redução de CPC = <strong className="text-primary">R$1.070/mês economizado</strong>.<br/>
              Quality Score recovery domain-wide = potencial <strong className="text-primary">5-10%</strong> nos próximos 3 meses.
            </div>

            <div className="grid-2" style={{ marginTop: 24 }}>
              <div className="panel">
                <div className="kpi-label">Intervenções técnicas</div>
                <ul style={{ marginTop: 12, paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.9 }}>
                  <li><strong>LiteSpeed ⇄ Cloudflare auto-purge</strong> (9 sites) — invalidação sincronizada origem-borda</li>
                  <li><strong>Elementor edge cache sync</strong> — Page Rules + purge on save (zerou stale CSS pós-edit)</li>
                  <li><strong>mu-plugins padronizados</strong> — preload, GF fix race-condition, WAF, YT facade, bypass admin</li>
                  <li><strong>WebP</strong> em domínios principais + Schema.org cleanup</li>
                  <li>Redução de plugins ativos (10-50+ deletados por site)</li>
                  <li>Page Rules CF (<code>*.blips.com.br/wp-admin*</code> → bypass)</li>
                </ul>
              </div>
              <div className="panel">
                <div className="kpi-label">Cloudflare hoje absorve</div>
                <div className="terminal-block" style={{ marginTop: 12 }}>
{`Requests/mês       25,9M
Cache hit ratio    77-90%
Origin tocado      <25% das requests
Quality Score      sustentado 8-9% form conv.
CF Business        $200/mês · ativo desde Dez/25`}
                </div>
                <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 12, lineHeight: 1.6 }}>
                  Cobertura: WAF Managed Rules + OWASP Core + Credential leak detection + headers de segurança.
                </p>
              </div>
            </div>

            <div className="callout amber">
              <strong>Sobre 9 LPs (3,5%) com variação negativa:</strong> Construcao (−6,8) e pizza (−3,0) totalizam 9 LPs. Lighthouse tem margem natural de ±5-10 pontos por run. Em investigação.
            </div>

            <div className="callout green">
              <strong>Upside:</strong> Score mobile médio <strong>+10 pts</strong> com Schema.org Product em 100% (atual: 30%), LCP &lt;2,5s em 100% (atual: 65%), WebP em 100% (atual: 70%) → <strong className="text-primary">+R$ 100-150k/mês adicionais</strong> em receita potencial.
            </div>
          </section>

          {/* SEC-07 — CYBER */}
          <section id="sec-07" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-07] // CYBER SECURITY</span>
              <h2 className="section-title glitch-hover">Incidentes Contidos · 6 Cadeias de Ataque</h2>
              <p className="section-subtitle">19 malwares · 36k+ posts spam · pipeline preservada · LGPD/ANPD em alerta</p>
            </div>

            <div className="panel">
              <div className="kpi-label">Timeline consolidada (jan/26 → mai/26) · 6 incidentes</div>
              <table className="mc">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Incidente</th>
                    <th>Vetor</th>
                    <th>Pipeline / Receita em risco</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="num">Nov-Dez/25</td>
                    <td><strong>SQLi/XSS Gravity Forms</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>proxies residenciais + headless</span></td>
                    <td>Forms abertos · WAF padrão CF não pegava</td>
                    <td className="text-primary"><strong>2.000 leads reprocessados via fallback</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>~150 CQLs · ~11 deals · ≈R$110k recuperados</span></td>
                  </tr>
                  <tr>
                    <td className="num">Nov/25</td>
                    <td><strong>Race condition GF + LiteSpeed</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>conflito gf_global + SyntaxError</span></td>
                    <td>Otimização LiteSpeed quebrando form submission</td>
                    <td className="text-danger"><strong>Forms quebrados = 0 leads no período</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>+ sobrecarga Make tentando reprocessar</span></td>
                  </tr>
                  <tr>
                    <td className="num">30/01/26</td>
                    <td><strong>SEO Spam promo.idealdistribuidora</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>casino hack — grego/russo</span></td>
                    <td>WordPress vulnerable · sem 2FA · sem Wordfence Pro</td>
                    <td className="text-primary"><strong>73 páginas spam removidas antes de escalar</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>prevenção · ~R$300k em SEO loss evitado</span></td>
                  </tr>
                  <tr>
                    <td className="num">04-10/02/26</td>
                    <td><strong>SEO mass injection (escalada)</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>5 subdomínios atingidos</span></td>
                    <td>Mesma porta — exploração em escala</td>
                    <td className="text-danger"><strong>33.027 posts cassino injetados</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>fitness 7.920 · sorvete 7.476 · pizza 7.354 · construcao 7.141 · ajuda 4.146</span></td>
                  </tr>
                  <tr>
                    <td className="num">Abr/26</td>
                    <td><strong>INC-2026-0401 — Trigger MySQL</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>backdoor invisível em PHP scans</span></td>
                    <td>Gravity Forms 2.8.13 SQLi (CVE)</td>
                    <td className="text-primary"><strong>1ª limpeza · trigger removido</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>porta não fechada · replantio em 30 dias</span></td>
                  </tr>
                  <tr>
                    <td className="num">07-12/05/26</td>
                    <td><strong>INC-2026-0508/0512 — Cadeia completa</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>FTP→trigger→C2→SEO poisoning</span></td>
                    <td>FTP comprometido (4 contas) · GF SQLi · webshells</td>
                    <td className="text-danger"><strong>19 malwares · 1.214 posts spam · C2 dropper 4 anos ativo</strong><br/><span className="text-muted" style={{ fontSize: '0.75rem' }}>16 webshells Tiny File Manager + 1 C2 (IP externo 63.141.248.210) + 2 autologin</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid-2" style={{ marginTop: 24 }}>
              <div className="panel">
                <div className="kpi-label">Pipeline preservada (acumulado)</div>
                <table className="mc" style={{ marginTop: 8 }}>
                  <tbody>
                    <tr><td>Leads reprocessados (WAF nov/25)</td><td className="num text-primary">~2.000</td></tr>
                    <tr><td>Posts spam removidos (5 INCs)</td><td className="num text-primary">~36.300</td></tr>
                    <tr><td>Malwares removidos</td><td className="num text-primary">19</td></tr>
                    <tr><td>SEO penalty evitado (proj.)</td><td className="num text-primary">R$ 3,5-6M / 3 anos</td></tr>
                    <tr><td>Receita estimada protegida</td><td className="num text-primary"><strong>R$ 110k + R$ 4M proj.</strong></td></tr>
                  </tbody>
                </table>
              </div>
              <div className="panel">
                <div className="kpi-label">Resposta arquitetural (4 camadas)</div>
                <ul style={{ marginTop: 12, paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.88rem', lineHeight: 1.9 }}>
                  <li><strong>WAF Cloudflare Business</strong> ($200/mês · Managed + OWASP)</li>
                  <li><strong>WAF Customizado client-side JS</strong> (intercepta SQLi/XSS antes do servidor + envia pra n8n auditoria)</li>
                  <li><strong>Cloudflare Turnstile</strong> (CAPTCHA invisível) + Honeypot via <code>functions.php</code></li>
                  <li><strong>Suite SCAN-BLIPS V4</strong> (cron · 15 camadas de detecção · 12 sites cobertos)</li>
                  <li><strong>BLIPS Toolkit V5</strong> (WP-CLI · hardening + manutenção)</li>
                </ul>
              </div>
            </div>

            <div className="panel" style={{ marginTop: 24 }}>
              <div className="kpi-label">Status atual dos vetores</div>
              <table className="mc" style={{ marginTop: 8 }}>
                <tbody>
                  <tr><td>FTP comprometido</td><td><span className="status-tag green"><span className="dot green"></span>FECHADO</span></td><td className="text-muted">4 contas rotacionadas (mai/12)</td></tr>
                  <tr><td>Gravity Forms SQLi</td><td><span className="status-tag green"><span className="dot green"></span>2.10.2</span></td><td className="text-muted">9 domínios atualizados</td></tr>
                  <tr><td>MySQL Triggers</td><td><span className="status-tag green"><span className="dot green"></span>LIMPO</span></td><td className="text-muted">2 triggers removidos · 0 ativos</td></tr>
                  <tr><td>xmlrpc.php</td><td><span className="status-tag green"><span className="dot green"></span>BLOQUEADO</span></td><td className="text-muted">8 domínios · 403 em system.multicall</td></tr>
                  <tr><td>Webshells</td><td><span className="status-tag green"><span className="dot green"></span>0 ATIVOS</span></td><td className="text-muted">19 removidos · scan 5 cam. limpo</td></tr>
                  <tr><td>C2 dropper</td><td><span className="status-tag green"><span className="dot green"></span>REMOVIDO</span></td><td className="text-muted">IP bloqueado · forense salva</td></tr>
                  <tr><td>WAF client + edge</td><td><span className="status-tag green"><span className="dot green"></span>ATIVO</span></td><td className="text-muted">CF Pro + JS interceptor + n8n</td></tr>
                  <tr><td>2FA admins WordPress</td><td><span className="status-tag amber"><span className="dot amber"></span>PARCIAL</span></td><td className="text-muted">Wordfence ativado · alguns admins pendentes</td></tr>
                  <tr><td>Backups SQL públicos</td><td><span className="status-tag green"><span className="dot green"></span>BLOQUEADOS</span></td><td className="text-muted">9 arquivos · 468MB · .htaccess deny</td></tr>
                </tbody>
              </table>
            </div>

            <div className="panel" style={{ marginTop: 24 }}>
              <div className="kpi-label">Jun/2026 — frentes ativas</div>
              <table className="mc" style={{ marginTop: 8 }}>
                <tbody>
                  <tr>
                    <td className="num" style={{ opacity: 0.6, fontSize: '0.78rem' }}>01/06/26</td>
                    <td><strong>INC-2026-0601 — pgAdmin exposto</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>~21 dias sem auth · bd.growth.blips.marketing · desktop mode + rota Traefik aberta</span></td>
                    <td><span className="status-tag green"><span className="dot green"></span>MITIGADO</span></td>
                  </tr>
                  <tr>
                    <td className="num" style={{ opacity: 0.6, fontSize: '0.78rem' }}>02/06/26</td>
                    <td><strong>WP Plugins — hardening suite atualizado</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>deploy mu-plugins faltando em estetica + confeccao · audit 9 sites · DISALLOW_FILE_EDIT</span></td>
                    <td><span className="status-tag green"><span className="dot green"></span>ENTREGUE</span></td>
                  </tr>
                  <tr>
                    <td className="num" style={{ opacity: 0.6, fontSize: '0.78rem' }}>02/06/26</td>
                    <td><strong>Google Ads — Fiore Di Napoli</strong><br/><span className="text-muted" style={{ fontSize: '0.78rem' }}>estratégia de campanha 2B: estrutura, segmentação, estimativas de CPL</span></td>
                    <td><span className="status-tag blue"><span className="dot" style={{ background: 'var(--secondary)', boxShadow: '0 0 8px var(--secondary)' }}></span>EM EXECUÇÃO</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="callout green">
              <strong>Upside:</strong> Auto-remediação ativa (atual: detecção semi-manual via cron) + WAF customizado por endpoint + 2FA Wordfence obrigatório em 100% dos admins + Mautic 4.4.9 EOL → 5.x + revisão trimestral de admins.
            </div>
          </section>

          {/* SEC-08 — SEO CLEANUP */}
          <section id="sec-08" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-08] // SEO CLEANUP</span>
              <h2 className="section-title glitch-hover">Suite SCAN-BLIPS · R$ 3,5–6M evitados</h2>
              <p className="section-subtitle">331 URLs cassino removidas · 3h trabalho · ROI 1.000-1.700×</p>
            </div>

            <div className="panel">
              <div className="kpi-label">Diagnóstico maio/2026</div>
              <table className="mc">
                <thead>
                  <tr>
                    <th>Subdomínio</th>
                    <th className="num">URLs spam</th>
                    <th>Status orgânico (28d)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>estetica.blips.com.br</td><td className="num text-danger"><strong>145</strong></td><td><span className="status-tag red"><span className="dot red"></span>0 CLIQUES · PENALIZADO</span></td></tr>
                  <tr><td>construcao.blips.com.br</td><td className="num">76</td><td><span className="status-tag amber">342 cliques · parcial</span></td></tr>
                  <tr><td>pizza.blips.com.br</td><td className="num">17</td><td><span className="status-tag amber">149 cliques · parcial</span></td></tr>
                  <tr className="highlight"><td><strong>TOTAL</strong></td><td className="num"><strong>331</strong></td><td>—</td></tr>
                </tbody>
              </table>
            </div>

            <div className="callout red">
              <strong>Smoking gun — Estética:</strong> 34,1% do inventário público era spam de cassino. CPL Meta inflado em <strong>3,8× a média</strong> (R$178 vs R$47). Quality Score destruído. R$83k/mês de verba ativa com retorno comprometido.
            </div>

            <div className="grid-2" style={{ marginTop: 24 }}>
              <div className="panel">
                <div className="kpi-label">Ações em 2026-05-18 (3h)</div>
                <ul style={{ marginTop: 12, paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 2 }}>
                  <li>410 Gone deployed em 4 sites (.htaccess)</li>
                  <li>7 sitemaps re-submetidos via GSC API</li>
                  <li>URL Removal Request via GSC UI</li>
                  <li>Suite SCAN-BLIPS ativada no cron</li>
                  <li>Backup .htaccess.bak.2026-05-18 em todos</li>
                </ul>
              </div>
              <div className="panel">
                <div className="kpi-label">Projeção 6 meses pós-limpeza</div>
                <table className="mc" style={{ marginTop: 8 }}>
                  <tbody>
                    <tr><td>M3 (jul)</td><td className="num">480 cliques/mês</td><td className="num text-primary">R$ 90k</td></tr>
                    <tr><td>M6 (out)</td><td className="num">1.400 cliques/mês</td><td className="num text-primary">R$ 230k</td></tr>
                    <tr className="highlight"><td><strong>Cumulativo 6m</strong></td><td>—</td><td className="num text-primary"><strong>~R$ 700k</strong></td></tr>
                    <tr><td>Stretch (Fase 2)</td><td>—</td><td className="num text-primary">R$ 1,1M</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="callout green">
              <strong>ROI da limpeza:</strong> 3h trabalho (~R$600 custo) → R$ 700k–1,1M recuperação = <strong className="text-primary">1.000–1.700×</strong>.
            </div>
          </section>

          {/* SEC-09 — UPSIDE */}
          <section id="sec-09" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-09] // POSSIBILIDADE DESTRAVÁVEL</span>
              <h2 className="section-title glitch-hover">Matriz de Upside</h2>
              <p className="section-subtitle">R$ 280-340k/mês em valor de mídia equivalente · R$ 3,4-4,1M/ano</p>
            </div>

            <div className="panel">
              <table className="mc">
                <thead>
                  <tr>
                    <th>Frente</th>
                    <th>Estado atual</th>
                    <th>Upside destravável</th>
                    <th className="num">Δ / mês</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>CQL%</strong></td>
                    <td>67,6% (mai/26)</td>
                    <td>80%+ recuperado · +1.000 CQLs/mês</td>
                    <td className="num text-primary"><strong>R$ 55k</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Custo/CQL</strong></td>
                    <td>R$55 (abr/26)</td>
                    <td>R$40-45 (−25% a −27%)</td>
                    <td className="num text-primary"><strong>R$ 110-170k</strong></td>
                  </tr>
                  <tr>
                    <td><strong>CPL Estética</strong></td>
                    <td>R$178/lead (3,8× média)</td>
                    <td>R$80-100 após Quality Score recovery</td>
                    <td className="num text-primary"><strong>R$ 25-35k</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Forms WP volume</strong></td>
                    <td>1.811 envios/mês (Ideal)</td>
                    <td>2.400-2.700 (escalando arquitetura)</td>
                    <td className="num text-primary"><strong>R$ 23-35k</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Fallback</strong></td>
                    <td>600 CNPJ/mês recorrente</td>
                    <td>1.200/mês (cross-domain + BD)</td>
                    <td className="num text-primary"><strong>R$ 23k</strong></td>
                  </tr>
                  <tr>
                    <td><strong>Performance</strong></td>
                    <td>+5,2 pts (251 LPs)</td>
                    <td>+10 pts médios</td>
                    <td className="num text-muted">indireto (QS)</td>
                  </tr>
                  <tr>
                    <td><strong>Cyber operacional</strong></td>
                    <td>Detecção semi-manual</td>
                    <td>Auto-remediação + WAF endpoint</td>
                    <td className="num text-muted">risco evitado</td>
                  </tr>
                  <tr>
                    <td><strong>SEO orgânico</strong></td>
                    <td>0 cliques estética</td>
                    <td>R$230k/mês em M6 (já em curso)</td>
                    <td className="num text-primary"><strong>R$ 230k</strong></td>
                  </tr>
                  <tr className="highlight">
                    <td colSpan={3}><strong>TOTAL RECEITA EXTRA / MÊS (conservador)</strong></td>
                    <td className="num text-primary"><strong>R$ 280-340k</strong></td>
                  </tr>
                  <tr className="highlight">
                    <td colSpan={3}><strong>TOTAL / ANO</strong></td>
                    <td className="num text-primary"><strong>R$ 3,4-4,1M</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="callout blue">
              Cada linha tem premissa explícita validável. Não conta benefício indireto (Quality Score recovery domain-wide). Não conta risco evitado (manual action SEO ≈ R$1,2M projetado). Valores em <strong>R$ de mídia equivalente</strong> — usar LTV/won para converter em receita.
            </div>

            <div className="callout amber">
              <strong>Bloqueio comum:</strong> capacidade de execução técnica em frentes simultâneas. Não há gargalo de orçamento ou estrutura.
            </div>
          </section>

          {/* SEC-10 — ROADMAP */}
          <section id="sec-10" className="mc">
            <div className="section-header">
              <span className="section-tag">[SEC-10] // ROADMAP 12 MESES</span>
              <h2 className="section-title glitch-hover">Plano de Execução</h2>
              <p className="section-subtitle">7 frentes mapeadas · sprint sizing · métricas de governança</p>
            </div>

            <div className="grid-3">
              <div className="panel">
                <div className="kpi-label text-secondary">Q3 2026 (jun-ago)</div>
                <ul style={{ marginTop: 12, paddingLeft: 16, color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.9 }}>
                  <li>Reescrita 7 LPs top10 (SEO B2B)<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>4-6 sprints</span></li>
                  <li>Lock pattern em 8 forms<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>2-3 sprints</span></li>
                  <li>Schema.org Product 100% LPs<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>3 sprints</span></li>
                  <li>2FA Wordfence + GSC cleanup<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>1 sprint</span></li>
                </ul>
              </div>
              <div className="panel">
                <div className="kpi-label text-primary">Q4 2026 (set-nov)</div>
                <ul style={{ marginTop: 12, paddingLeft: 16, color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.9 }}>
                  <li>Fallback cross-domain<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>3-4 sprints</span></li>
                  <li>Enriquecimento BD half-submissions<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>2 sprints</span></li>
                  <li>Mautic 4.4.9 → 5.x<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>2 sprints</span></li>
                  <li>Page Rules CF segmentadas<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>1 sprint</span></li>
                </ul>
              </div>
              <div className="panel">
                <div className="kpi-label text-alert">Q1 2027 (dez-fev)</div>
                <ul style={{ marginTop: 12, paddingLeft: 16, color: 'var(--text-muted)', fontSize: '0.85rem', lineHeight: 1.9 }}>
                  <li>Auto-remediação SCAN-BLIPS<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>4 sprints</span></li>
                  <li>WAF customizado por endpoint<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>3 sprints</span></li>
                  <li>Topic clustering (piloto sorvete)<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>4-6 sprints</span></li>
                  <li>Snapshot AFTER 6m + análise<br/><span className="text-muted" style={{ fontSize: '0.7rem' }}>1 sprint</span></li>
                </ul>
              </div>
            </div>

            <div className="panel" style={{ marginTop: 24 }}>
              <div className="kpi-label">Métricas de governança</div>
              <div className="grid-2" style={{ marginTop: 12 }}>
                <ul style={{ paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 2 }}>
                  <li>Snapshot SEO mensal (GSC + serp_products.json)</li>
                  <li>Funil report semanal (Pipedrive → planilha)</li>
                </ul>
                <ul style={{ paddingLeft: 18, color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 2 }}>
                  <li>Performance review trimestral (PageSpeed batch)</li>
                  <li>Cyber audit mensal (SCAN-BLIPS report)</li>
                </ul>
              </div>
            </div>

            <div className="callout green">
              <strong>Critério de sucesso 12 meses:</strong> Funil CQL% 67,6 → 82%+ · Custo/CQL R$55 → R$40-45 · Receita extra projetada validada R$ 3,5M+ em 12 meses.
            </div>

            <div className="callout blue" style={{ marginTop: 24 }}>
              <strong>Metodologia · validação com base histórica:</strong><br/>
              Premissas usadas: CQL% 76,4% · CQL→Won 7,1% · LTV B2B R$10k (placeholder).<br/>
              <strong>A validar via Pipedrive + Looker:</strong> connect rate por origem, conversão por form, LTV real B2B por segmento, atribuição multi-touch entre paid e SEO orgânico recuperado.
            </div>

            <div className="panel" style={{ marginTop: 40, padding: '40px 32px', borderColor: 'var(--primary)', borderWidth: '2px', textAlign: 'center' }}>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '1.5rem', lineHeight: 1.5, color: 'var(--text-main)', margin: 0 }}>
                A pergunta para a diretoria:
              </p>
              <p style={{ fontFamily: 'var(--font-heading)', fontSize: '1.7rem', lineHeight: 1.5, color: 'var(--text-main)', marginTop: 24 }}>
                Quanto vale <span className="text-primary">R$ 3,4-4,1M/ano</span> em receita destravável<br/>
                quando o sangramento já evitado é de <span className="text-primary">R$ 30-50k/dia</span>?
              </p>
              <p style={{ marginTop: 32, fontFamily: 'var(--font-mono)', fontSize: '1.05rem', color: 'var(--text-muted)', lineHeight: 1.8 }}>
                A engenharia já está plantada.<br/>
                O próximo destrave é <span className="text-primary">decisão da casa</span>.
              </p>
            </div>
          </section>

        </main>
      </div>

      <div className="status-bar">
        <div>
          <span className="text-muted">BLIPS</span> · <span className="text-primary">GROWTH ENGINEERING</span> · <span className="text-muted">v1.0</span>
        </div>
        <div className="text-muted">
          {SECTIONS.findIndex(s => s.id === activeSection) + 1} / {SECTIONS.length} · {progress.toFixed(0)}% · ↑↓ keys
        </div>
      </div>
    </div>
  )
}
